import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

// OAuth / legacy age-gate backfill (server flag: profile.needsBirthYear). Accounts that never
// passed the register age gate (Google/Apple sign-ups, pre-gate accounts) must answer the same
// age-neutral birth-year question before using the app — this modal BLOCKS the shell (no scrim
// dismiss, no cancel) until answered. The backend stores the year write-once; if it implies
// under 13 the response carries { underage: true } (and the account is auto-suspended
// server-side) → show the friendly guardian message, best-effort DELETE /auth/account, sign out.
export default function BirthYearGate() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [birthYear, setBirthYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false); // answered 13+ → gate lifts
  const [underage, setUnderage] = useState(false);

  const thisYear = new Date().getUTCFullYear();

  const submit = async (e) => {
    e.preventDefault();
    const year = Number(birthYear);
    if (!/^\d{4}$/.test(birthYear) || year < 1900 || year > thisYear) {
      setError('Enter the year you were born.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const res = await api.patch('/auth/profile', { birthYear: year });
      if (res.data?.underage) {
        setUnderage(true);
      } else {
        setSaved(true);
      }
    } catch {
      setError('Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // Underage flow: the account can't be used — delete it (best-effort; the backend suspended it
  // regardless) and sign out.
  const finishUnderage = async () => {
    setBusy(true);
    try { await api.delete('/auth/account'); } catch { /* best-effort — it's suspended anyway */ }
    try { await signOut(); } catch { /* ignore */ }
    navigate('/', { replace: true });
  };

  if (saved) return null;

  return createPortal(
    <div className="db-modal-scrim">
      <div className="db-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        {underage ? (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: 20 }}>We can’t keep this account</h2>
            <p style={{ color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 14px' }}>
              Thanks for being honest. SphereSync accounts need a parent or guardian at your age —
              they can add you as a managed member from their own account (Profile → Guardian), so
              you can still share moments with your circles. This account will now be removed and
              you’ll be signed out.
            </p>
            <div className="db-row" style={{ justifyContent: 'flex-end' }}>
              <button className="db-btn db-btn-primary" onClick={finishUnderage} disabled={busy}>
                {busy ? 'Signing out…' : 'OK'}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>One quick thing</h2>
            <p style={{ color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 12px' }}>
              Before you continue, we need the year you were born — every SphereSync account
              answers this once.
            </p>
            <label className="db-field">
              <span>Birth year</span>
              <input
                type="text" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} required autoFocus
                value={birthYear}
                onChange={(e) => { setBirthYear(e.target.value.replace(/\D/g, '')); setError(null); }}
                placeholder="YYYY"
              />
            </label>
            {error && <div className="db-auth-error" style={{ marginBottom: 10 }}>{error}</div>}
            <div className="db-row" style={{ justifyContent: 'flex-end' }}>
              <button className="db-btn db-btn-primary" type="submit" disabled={busy || birthYear.length !== 4}>
                {busy ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
